import { createCareIndustryPages } from './careIndustryPages.js';
import { createRetailIndustryPages } from './retailIndustryPages.js';
import { createApplicationPage } from './applicationPageContent.js';
import { createNewIndustryPages } from './industryPageContent.js';
import type { StaticPageContent, StaticPageKey } from './staticPageContent.js';
import type { StaticPageDefaultDataDependencies } from './staticPageDefaultDataTypes.js';

export const createStaticPageApplicantDefaults = (
  dependencies: StaticPageDefaultDataDependencies,
): Record<StaticPageKey, StaticPageContent> => {
  const { applicationPortal, landingPageContentRepository, baseContent, industryCare, industryCraft, industryGastro, industryRetail, groupPhoto, partnerGlobus, globusGemeinsam, globusMarkthalle, missionImage, globusJobsSection, globusJobs, globusJobsOverview, missionStoryContent } = dependencies;
  const pages: Record<StaticPageKey, StaticPageContent> = {
    ...createNewIndustryPages(),
    ...createRetailIndustryPages(),
    ...createCareIndustryPages(),
    bewerben: createApplicationPage(),
    unternehmen: {
      key: 'unternehmen',
      eyebrow: 'Arbeitgeber verstehen',
      title: 'Lerne die Arbeitgeber kennen, bei denen du wirklich ankommen kannst.',
      intro:
        'Auf dieser Seite zeigen wir dir, wie Viva Porta passende Kliniken, Pflegeeinrichtungen und Gesundheitsunternehmen auswählt. Du sollst nicht nur irgendeinen Vertrag bekommen, sondern einen Arbeitsplatz, der fachlich, menschlich und organisatorisch zu deinem Start in Deutschland passt.',
      heroImage: landingPageContentRepository.heroImage,
      cards: [
        {
          iconClass: 'icon-[material-symbols--manage-search]',
          title: 'Passende Einrichtungen',
          text: 'Wir prüfen, welche Arbeitgeber internationale Pflegekräfte gut aufnehmen können: Teamstruktur, Dienstplanung, Einarbeitung, Wohnsituation und Erfahrung mit Anerkennungsverfahren.',
        },
        {
          iconClass: 'icon-[material-symbols--contract-edit]',
          title: 'Transparenter Vertrag',
          text: 'Du erfährst früh, wo du arbeiten würdest, welche Aufgaben dich erwarten und welche Rahmenbedingungen gelten. Keine unklaren Zusagen, sondern nachvollziehbare nächste Schritte.',
        },
        {
          iconClass: 'icon-[material-symbols--forum]',
          title: 'Vorbereitete Gespräche',
          text: 'Wir bereiten dich auf Interviews mit deutschen Arbeitgebern vor, erklären typische Fragen und begleiten dich dabei, deine Erfahrung sicher zu präsentieren.',
        },
        {
          iconClass: 'icon-[material-symbols--diversity-1]',
          title: 'Teamfit zählt',
          text: 'Ein guter Start hängt nicht nur vom Fachbereich ab. Wir achten darauf, ob Erwartungen, Kommunikation und persönliche Ziele zum Arbeitgeber passen.',
        },
        {
          iconClass: 'icon-[material-symbols--home-work]',
          title: 'Ankommen im Alltag',
          text: 'Wohnung, Anmeldung, erste Wege und Orientierung vor Ort werden mitgedacht. So wird aus dem Jobangebot ein realistischer Start in Deutschland.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Begleitung nach dem Start',
          text: 'Auch nach dem ersten Arbeitstag bleiben wir ansprechbar, wenn Fragen im Team, bei Behörden oder im Alltag entstehen.',
        },
      ],
    },
    pflege: {
      key: 'pflege',
      eyebrow: 'Branchenlösung Pflege',
      title: 'Pflege braucht Menschen mit Zukunft.',
      intro: 'Wir bringen Pflegeeinrichtungen mit internationalen Fachkräften zusammen. Sprache, Anerkennung und den Start im Team begleiten wir persönlich.',
      heroStageTitle: 'Pflegekräfte, die bleiben.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.pflege },
      heroImage: industryCare,
      heroHighlights: ['Sprachliche Vorbereitung', 'Anerkennung begleitet', 'Persönliche Integration'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--medical-services]',
          title: 'Fachlich vorgeprüft',
          text: 'Ausbildung, Berufserfahrung, Motivation und Anerkennungsfähigkeit werden vor einer Vorstellung nachvollziehbar geprüft.',
        },
        {
          iconClass: 'icon-[material-symbols--translate]',
          title: 'Sprache mit Berufsbezug',
          text: 'Die Vorbereitung richtet sich nicht nur auf Prüfungen, sondern auch auf Kommunikation im Pflegealltag.',
        },
        {
          iconClass: 'icon-[material-symbols--approval-delegation]',
          title: 'Anerkennung begleitet',
          text: 'Unterlagen, Übersetzungen, Anerkennungsantrag, Visum und Arbeitserlaubnis werden strukturiert koordiniert.',
        },
      ],
      sections: [
        {
          eyebrow: 'Herausforderung',
          title: 'Pflege braucht Verlässlichkeit, nicht nur kurzfristige Besetzung.',
          text:
            'Dienstpläne, Pflegequalität und Teamstabilität geraten unter Druck, wenn offene Stellen dauerhaft nicht besetzt werden. Gleichzeitig sind Anerkennung, Sprache, Einarbeitung und Bleibeperspektive entscheidend dafür, ob internationale Gewinnung wirklich nachhaltig funktioniert.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--schedule]',
              title: 'Planungsdruck',
              text: 'Ausfälle und offene Stellen belasten Dienstplanung, Teams und Versorgungsqualität.',
            },
            {
              iconClass: 'icon-[material-symbols--workspace-premium]',
              title: 'Formale Hürden',
              text: 'Anerkennung und Behördenkommunikation müssen früh und sauber vorbereitet werden.',
            },
            {
              iconClass: 'icon-[material-symbols--diversity-3]',
              title: 'Integration',
              text: 'Ein guter Start entscheidet sich im Alltag: Wohnen, Teamkommunikation, Einarbeitung und Begleitung.',
            },
          ],
        },
        {
          eyebrow: 'Viva Porta-Lösung',
          title: 'Ein strukturierter Weg von der Auswahl bis zum Ankommen im Team.',
          text:
            'Viva Porta verbindet internationale Rekrutierung mit persönlicher Begleitung. Einrichtungen erhalten passende Profile, klare Statusupdates und Unterstützung bei den Prozessschritten, die intern besonders viel Zeit binden.',
        },
      ],
      faq: [
        {
          question: 'Wie lange dauert eine Vermittlung in der Pflege?',
          answer:
            'Internationale Vermittlung braucht Vorlauf. Entscheidend sind Sprache, Anerkennung, Visum und Starttermin. Viva Porta arbeitet deshalb mit realistischen Meilensteinen statt kurzfristigen Versprechen.',
        },
        {
          question: 'Welche Rolle spielt die Anerkennung?',
          answer:
            'Sie ist zentral für eine qualifizierte Tätigkeit. Wir prüfen Unterlagen früh, koordinieren Übersetzungen und begleiten den Antrag bis zur belastbaren Entscheidung.',
        },
      ],
      cta: {
        eyebrow: 'Personalbedarf Pflege',
        title: 'Lassen Sie uns Ihren Bedarf realistisch einschätzen.',
        text: 'Wir prüfen gemeinsam, welche Profile zu Ihrer Einrichtung passen und welche Schritte für eine nachhaltige Besetzung sinnvoll sind.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.pflege },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    gastro: {
      key: 'gastro',
      eyebrow: 'Branchenlösung Gastro',
      title: 'Gastro-Teams, die Tempo mittragen.',
      intro: 'Für Küche und Service finden wir internationale Mitarbeitende, die zu Ihrem Betrieb passen. Aufgaben, Sprachkenntnisse und Teamalltag klären wir von Anfang an.',
      heroStageTitle: 'Teams, die Tempo mittragen.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.gastro },
      heroImage: industryGastro,
      heroHighlights: ['Serviceorientierung', 'Teamfähigkeit', 'Sprachliche Vorbereitung'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--restaurant]',
          title: 'Service und Küche',
          text: 'Profile für Service, Küche, Housekeeping und operative Rollen werden passend zum Betrieb vorqualifiziert.',
        },
        {
          iconClass: 'icon-[material-symbols--event]',
          title: 'Saison planbar machen',
          text: 'Frühzeitige Vorbereitung hilft, saisonale Spitzen und wiederkehrende Engpässe besser abzufedern.',
        },
        {
          iconClass: 'icon-[material-symbols--groups]',
          title: 'Teamfit beachten',
          text: 'Motivation, Sprachstand, Erfahrung und Erwartung werden vorab mit dem Betrieb abgeglichen.',
        },
      ],
      sections: [
        {
          eyebrow: 'Herausforderung',
          title: 'Gute Gästeerlebnisse hängen an stabilen Teams.',
          text:
            'In Gastronomie und Hotellerie treffen wechselnde Nachfrage, lange Öffnungszeiten und hohe Fluktuation auf steigende Erwartungen an Servicequalität. Internationale Gewinnung kann helfen, wenn sie seriös vorbereitet und menschlich begleitet wird.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--schedule]',
              title: 'Öffnungszeiten',
              text: 'Schichten, Wochenenden und saisonale Spitzen erfordern verlässliche Personalplanung.',
            },
            {
              iconClass: 'icon-[material-symbols--thumb-up]',
              title: 'Servicequalität',
              text: 'Gäste merken sofort, wenn Teams überlastet oder Rollen unklar besetzt sind.',
            },
            {
              iconClass: 'icon-[material-symbols--language]',
              title: 'Kommunikation',
              text: 'Sprachliche Vorbereitung und klare Erwartungen sind im Gästekontakt besonders wichtig.',
            },
          ],
        },
        {
          eyebrow: 'Viva Porta-Lösung',
          title: 'Internationale Personalgewinnung mit klarem Rollenprofil.',
          text:
            'Wir starten mit Ihrem konkreten Bedarf: Betriebskonzept, Arbeitszeiten, Unterkunftsmöglichkeiten, Sprachbedarf und Einsatzbereich. Daraus entsteht eine realistische Auswahl statt einer anonymen Bewerberliste.',
        },
      ],
      faq: [
        {
          question: 'Eignet sich Viva Porta auch für saisonale Betriebe?',
          answer:
            'Ja, wenn der Bedarf früh genug planbar ist. Wir prüfen gemeinsam, welche Rollen, Starttermine und Aufenthaltswege realistisch sind.',
        },
        {
          question: 'Welche Sprachkenntnisse sind sinnvoll?',
          answer:
            'Das hängt von der Rolle ab. Service und Rezeption brauchen andere Kommunikation als Küche oder Housekeeping. Genau deshalb wird der Sprachbedarf im Profil geklärt.',
        },
      ],
      cta: {
        eyebrow: 'Personalbedarf Gastro',
        title: 'Planen Sie die nächste Saison früher und verlässlicher.',
        text: 'Wir besprechen, welche internationalen Profile zu Ihrem Betrieb passen und wie ein realistischer Zeitplan aussieht.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.gastro },
        secondaryAction: { label: 'Branchen ansehen', href: '/#branchen' },
      },
    },
    handwerk: {
      key: 'handwerk',
      eyebrow: 'Branchenlösung Handwerk',
      title: 'Fachkräfte fürs Handwerk von morgen.',
      intro: 'Wir finden internationale Fachkräfte, die zu Ihrem Handwerksbetrieb passen. Qualifikation, Sprache und die Vorbereitung auf den Arbeitsalltag betrachten wir gemeinsam.',
      heroStageTitle: 'Fachkräfte fürs Handwerk.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.handwerk },
      heroImage: industryCraft,
      heroHighlights: ['Strukturierte Auswahl', 'Langfristige Bindung', 'Begleitete Integration'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--construction]',
          title: 'Qualifikation prüfen',
          text: 'Berufserfahrung, Ausbildungswege und praktische Einsatzfähigkeit werden vorab eingeordnet.',
        },
        {
          iconClass: 'icon-[material-symbols--handshake]',
          title: 'Betrieblich passend',
          text: 'Kleine und mittlere Betriebe brauchen Menschen, die fachlich und menschlich ins Team passen.',
        },
        {
          iconClass: 'icon-[material-symbols--home-work]',
          title: 'Ankommen begleiten',
          text: 'Wohnung, Behörden und Alltagsfragen werden mitgedacht, damit der Start nicht am Organisatorischen scheitert.',
        },
      ],
      sections: [
        {
          eyebrow: 'Herausforderung',
          title: 'Handwerk braucht Nachwuchs, Erfahrung und Menschen, die bleiben.',
          text:
            'Auftragsbücher, Kundenbindung und Betriebsnachfolge hängen davon ab, ob Betriebe qualifizierte Mitarbeitende finden. Internationale Fachkräfte können eine Brücke sein, wenn Qualifikation, Sprache und Integration realistisch geplant werden.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--work]',
              title: 'Engpässe im Alltag',
              text: 'Fehlende Fachkräfte bremsen Termine, Projekte und Wachstum.',
            },
            {
              iconClass: 'icon-[material-symbols--school]',
              title: 'Qualifikation',
              text: 'Ausbildungssysteme unterscheiden sich. Deshalb braucht es sorgfältige Prüfung und klare Rollen.',
            },
            {
              iconClass: 'icon-[material-symbols--diversity-1]',
              title: 'Teamnähe',
              text: 'Gerade im Handwerk zählt Vertrauen im kleinen Team und bei Kundenterminen.',
            },
          ],
        },
        {
          eyebrow: 'Viva Porta-Lösung',
          title: 'Eine Brücke zwischen Betrieb, Fachkraft und Alltag in Deutschland.',
          text:
            'Viva Porta unterstützt bei Vorauswahl, Unterlagen, Sprach- und Integrationsplanung. Ziel ist kein schneller Lebenslauf, sondern ein realistischer Start in einem Betrieb, der langfristig denkt.',
        },
      ],
      faq: [
        {
          question: 'Für welche Gewerke ist die Vermittlung geeignet?',
          answer:
            'Grundsätzlich für Betriebe mit klar beschreibbaren Rollen und planbarem Bedarf. Die genaue Eignung hängt von Qualifikation, Anerkennungserfordernissen und Einsatzprofil ab.',
        },
        {
          question: 'Wie wird der Teamfit geprüft?',
          answer:
            'Neben fachlichen Unterlagen zählen Motivation, Erwartung, Sprachstand und die Bereitschaft, in Deutschland langfristig Fuß zu fassen.',
        },
      ],
      cta: {
        eyebrow: 'Personalbedarf Handwerk',
        title: 'Sprechen wir über Ihren Betrieb und die passenden Profile.',
        text: 'Wir ordnen gemeinsam ein, welche Qualifikationen gebraucht werden und welcher Weg zur internationalen Gewinnung realistisch ist.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.handwerk },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    einzelhandel: {
      key: 'einzelhandel',
      eyebrow: 'Branchenlösung Einzelhandel',
      title: 'Menschen, die den Einzelhandel stärken.',
      intro: 'Ob Verkauf, Beratung oder Frischetheke: Wir unterstützen Sie bei der Suche nach internationalen Mitarbeitenden und begleiten den Einstieg in Ihr Team.',
      heroStageTitle: 'Menschen, die Märkte stärken.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.einzelhandel },
      heroImage: industryRetail,
      heroHighlights: ['Kundenorientierung', 'Frischebereiche', 'Langfristige Integration'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--storefront]',
          title: 'Filialbedarf verstehen',
          text: 'Standort, Öffnungszeiten, Sortiment, Kundennähe und Teamstruktur werden in der Suche berücksichtigt.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Kundenkontakt vorbereiten',
          text: 'Für Service- und Verkaufsrollen werden Sprache, Auftreten und Erwartung besonders sorgfältig betrachtet.',
        },
        {
          iconClass: 'icon-[material-symbols--verified-user]',
          title: 'Nachhaltig besetzen',
          text: 'Ziel sind Mitarbeitende, die Standort, Rolle und Alltag verstehen und langfristig bleiben können.',
        },
      ],
      sections: [
        {
          eyebrow: 'Herausforderung',
          title: 'Einzelhandel braucht Menschen, die Verlässlichkeit und Kundennähe verbinden.',
          text:
            'Schichtmodelle, verlängerte Öffnungszeiten, Standortbindung und wechselnde Nachfrage machen Personalplanung anspruchsvoll. Internationale Arbeitskräfte können helfen, wenn der Einstieg klar geführt und realistisch vorbereitet wird.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--schedule]',
              title: 'Öffnungszeiten',
              text: 'Lange Ladenzeiten und Wochenenden brauchen stabile Teams.',
            },
            {
              iconClass: 'icon-[material-symbols--groups]',
              title: 'Fluktuation',
              text: 'Häufige Wechsel kosten Zeit, Wissen und Qualität im Kundenkontakt.',
            },
            {
              iconClass: 'icon-[material-symbols--location-on]',
              title: 'Standortbindung',
              text: 'Der Arbeitsplatz muss auch zum Wohnort, Alltag und sozialen Ankommen passen.',
            },
          ],
        },
        {
          eyebrow: 'Viva Porta-Lösung',
          title: 'Nachhaltige Personalgewinnung statt kurzfristiger Aushilfe.',
          text:
            'Wir klären früh, welche Rollen wirklich gebraucht werden, welche Sprach- und Serviceanforderungen bestehen und wie neue Mitarbeitende im Standort gut ankommen können.',
        },
        globusJobsSection(),
        {
          eyebrow: 'Frischebereiche',
          title: 'Frischetheken sind mehr als Besetzung: Sie prägen Frequenz, Beratung und Warenkorb.',
          text:
            'Für Vollsortimenter sind Fleisch-, Fisch- und Käsetheken strategische Kontaktpunkte. Wenn qualifizierte Mitarbeitende fehlen, geraten Öffnungszeiten, Beratung, Zusatzkäufe und die Abgrenzung zum Discounter unter Druck.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--storefront]',
              title: 'Frequenz und Bindung',
              text: 'Starke Frischebereiche bringen Kundinnen und Kunden in den Markt und schaffen persönliche Beratung statt reiner Selbstbedienung.',
            },
            {
              iconClass: 'icon-[material-symbols--sell]',
              title: 'Marge und Zusatzkäufe',
              text: 'Kompetente Bedienung unterstützt Premiumkäufe, spontane Entscheidungen und passende Zusatzartikel im Warenkorb.',
            },
            {
              iconClass: 'icon-[material-symbols--verified-user]',
              title: 'Hygiene und Produktsicherheit',
              text: 'In serviceintensiven Bereichen zählen sauberes Arbeiten, Produkthandling, Verlässlichkeit und klare Kommunikation im Team.',
            },
          ],
        },
      ],
      faq: [
        {
          question: 'Welche Rollen im Einzelhandel sind geeignet?',
          answer:
            'Das hängt vom Standort ab. Verkauf, Service, Lager und unterstützende Filialrollen können geeignet sein, wenn Aufgaben, Sprache und Einarbeitung klar beschrieben sind.',
        },
        {
          question: 'Wie wird langfristige Bindung unterstützt?',
          answer:
            'Durch realistische Erwartungen, persönliche Begleitung und frühe Klärung von Alltag, Wohnsituation und Teamkommunikation.',
        },
      ],
      cta: {
        eyebrow: 'Personalbedarf Einzelhandel',
        title: 'Besprechen wir Ihren Standort und die passenden Rollen.',
        text: 'Wir helfen, den Bedarf zu strukturieren und einen realistischen Weg für internationale Mitarbeitende zu planen.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.einzelhandel },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    globus: {
      key: 'globus',
      eyebrow: 'Exklusivpartner Globus',
      title: 'Globus und Viva Porta.',
      intro:
        'Globus sucht aktuell Menschen für handwerkliche Frischebereiche, Verkauf und direkten Kundenkontakt. Viva Porta begleitet internationale Bewerber:innen persönlich auf diesem Weg.',
      heroImage: globusGemeinsam,
      heroGallery: [
        {
          image: partnerGlobus,
          imageAlt: 'GLOBUS Logo',
          kind: 'logo',
        },
        {
          image: globusMarkthalle,
          imageAlt: 'GLOBUS Markthalle mit dem GLOBUS Schriftzug und Logo',
        },
      ],
      cards: [
        {
          iconClass: 'icon-[material-symbols--workspace-premium]',
          title: 'Exklusiver Rahmen',
          text: 'Viva Porta bündelt die Vorbereitung internationaler Bewerber:innen für Globus und achtet auf Sprache, Erwartungen und realistische Einstiegspunkte.',
        },
        {
          iconClass: 'icon-[material-symbols--storefront]',
          title: 'Starke Märkte',
          text: 'Globus steht für große Märkte mit starken Frischebereichen, sichtbarer Beratung und Teams, die nah an Kundinnen und Kunden arbeiten.',
        },
        {
          iconClass: 'icon-[material-symbols--diversity-1]',
          title: 'Ankommen im Alltag',
          text: 'Der Einstieg wird nicht nur als Job gedacht, sondern als Weg in ein Team, eine Stadt und einen neuen beruflichen Alltag.',
        },
      ],
      sections: [
        {
          eyebrow: 'Partnerschaft',
          title: 'Ein Partner, bei dem internationale Talente sichtbar werden.',
          text:
            'Globus ist für Viva Porta ein Exklusivpartner, weil hier praktische Erfahrung, Kundennähe und starke Frischebereiche zusammenkommen. Die Rollen sind konkret, greifbar und nah an den Fähigkeiten vieler Menschen, die in Deutschland neu starten möchten.',
          image: globusGemeinsam,
          imageAlt: 'Viva Porta und Globus gemeinsam bei der Partnerschaft',
        },
        {
          eyebrow: 'Kooperationsgeschichte',
          title: 'Aus einem Gespräch über offene Stellen wurde eine gemeinsame Aufgabe.',
          text:
            'Die Kooperation entstand aus einer einfachen Beobachtung: Globus braucht Menschen, die Frischebereiche mit Können und Freundlichkeit tragen; Viva Porta kennt internationale Bewerber:innen, die genau solche praktischen Rollen suchen. Aus ersten Gesprächen über einzelne Bedarfe wurde ein gemeinsamer Prozess: klare Jobprofile, ehrliche Vorbereitung, persönliche Begleitung und ein Start, der für Bewerber:innen und Marktteam gleichermaßen planbar ist.',
          image: partnerGlobus,
          imageAlt: 'Globus Logo',
        },
      ],
      globusJobs: globusJobs.map(job => ({
        ...job,
        captionTracks: job.captionTracks.map(track => ({ ...track })),
        tasks: [...job.tasks],
        requirements: [...job.requirements],
        benefits: [...job.benefits],
        cta: { ...job.cta },
        warning: job.warning ? { ...job.warning } : undefined,
      })),
      globusJobsOverview: {
        ...globusJobsOverview,
        sharedBenefits: [...globusJobsOverview.sharedBenefits],
      },
      cta: {
        eyebrow: 'Bewerbung Globus',
        title: 'Du erkennst dich in einer dieser Rollen wieder?',
        text: 'Dann melde dich bei Viva Porta. Wir prüfen gemeinsam, welche Globus-Rolle zu deiner Erfahrung passt und welche Schritte als Nächstes sinnvoll sind.',
        image: groupPhoto,
        imageAlt: 'Viva Porta zu Besuch im Metzgereibereich einer GLOBUS Markthalle',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.einzelhandel },
        secondaryAction: { label: 'Einzelhandel ansehen', href: '/branchen/einzelhandel' },
      },
    },
    ablauf: {
      key: 'ablauf',
      eyebrow: 'Ablauf für Bewerber:innen',
      title: 'Dein klarer Weg bis zum Arbeitsstart.',
      intro:
        'Viva Porta begleitet dich persönlich durch Bewerbung, Gespräche, Sprache, Anerkennung, Visum, Einreise und deinen Start in Deutschland.',
      heroImage: globusMarkthalle,
      cards: [
        {
          iconClass: 'icon-[material-symbols--person-search]',
          title: 'Erstes Gespräch',
          text: 'Wir lernen deine Ausbildung, Erfahrung, Sprachkenntnisse und Ziele kennen und sagen dir ehrlich, welcher Weg realistisch ist.',
        },
        {
          iconClass: 'icon-[material-symbols--inventory]',
          title: 'Unterlagen prüfen',
          text: 'Gemeinsam sammeln und prüfen wir Zeugnisse, Nachweise, Passdaten und alle Dokumente, die später für Anerkennung und Visum wichtig sind.',
        },
        {
          iconClass: 'icon-[material-symbols--forum]',
          title: 'Arbeitgeber kennenlernen',
          text: 'Wir schlagen passende Arbeitgeber vor, bereiten dich auf Gespräche vor und begleiten den Auswahlprozess transparent.',
        },
        {
          iconClass: 'icon-[material-symbols--translate]',
          title: 'Deutsch lernen',
          text: 'Du lernst Deutsch mit einem klaren Ziel: Alltag, Interview, Pflegefachsprache und das Sprachniveau, das für Anerkennung und Arbeit nötig ist.',
        },
        {
          iconClass: 'icon-[material-symbols--workspace-premium]',
          title: 'Anerkennung und Visum',
          text: 'Wir begleiten Übersetzungen, Anerkennungsantrag, Termine, Visa-Unterlagen und die Abstimmung mit Behörden.',
        },
        {
          iconClass: 'icon-[material-symbols--flight-land]',
          title: 'Einreise und Start',
          text: 'Vor deiner Reise klären wir Ankunft, Unterkunft, Anmeldung und die ersten Tage, damit du sicher in Deutschland startest.',
        },
      ],
    },
    'ueber-vivaporta': {
      key: 'ueber-vivaporta',
      eyebrow: 'Über Viva Porta',
      title: 'Eine Brücke zwischen Herkunft, Beruf und Ankommen.',
      heroTitleHighlight: 'Brücke',
      intro:
        'Viva Porta ist entstanden, weil internationale Vermittlung mehr braucht als Profile und Verträge: Verständnis für die Türkei, Klarheit über Deutschland und Menschen, die den Weg persönlich begleiten.',
      heroImage: missionImage,
      missionStory: missionStoryContent,
      cards: [
        {
          iconClass: 'icon-[material-symbols--handshake]',
          title: 'Fair und transparent',
          text: 'Du bekommst realistische Informationen zu Kosten, Dauer, Sprache, Anerkennung und Arbeitgebern. Wir versprechen nichts, was wir nicht erklären können.',
        },
        {
          iconClass: 'icon-[material-symbols--diversity-1]',
          title: 'Deutsch-türkische Begleitung',
          text: 'Wir verstehen beide Seiten: deine Situation in der Türkei und die Anforderungen in Deutschland. Dadurch entstehen weniger Missverständnisse und mehr Sicherheit.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Mehr als Vermittlung',
          text: 'Wir bleiben auch dann wichtig, wenn der Vertrag unterschrieben ist: Sprache, Behörden, Ankunft, Alltag und Start im Team gehören für uns dazu.',
        },
      ],
      sections: [
        {
          eyebrow: 'Unser Hintergrund',
          title: 'Wir kennen die Fragen, die zwischen zwei Ländern entstehen.',
          text:
            'Viele Bewerber:innen starten nicht mit einem fertigen Plan, sondern mit Unsicherheit: Welche Unterlagen zählen? Welches Deutschniveau ist realistisch? Wie fühlt sich ein erster Arbeitstag in Deutschland an? Genau hier liegt unser Hintergrund: Wir übersetzen nicht nur Sprache, sondern Erwartungen, Abläufe und kulturelle Unterschiede.',
          image: groupPhoto,
          imageAlt: 'Viva Porta Team bei einem gemeinsamen Besuch im Arbeitsalltag',
          cards: [
            {
              iconClass: 'icon-[material-symbols--translate]',
              title: 'Zweisprachige Nähe',
              text: 'Deutsch und Türkisch helfen nicht nur bei Dokumenten, sondern vor allem bei ehrlichen Gesprächen über Chancen, Grenzen und nächste Schritte.',
            },
            {
              iconClass: 'icon-[material-symbols--route]',
              title: 'Orientierung im Prozess',
              text: 'Anerkennung, Visum, Sprache, Arbeitgeberkontakt und Ankommen greifen ineinander. Wir erklären den Weg so, dass Entscheidungen nachvollziehbar bleiben.',
            },
            {
              iconClass: 'icon-[material-symbols--diversity-1]',
              title: 'Menschliche Begleitung',
              text: 'Hinter jeder Bewerbung steht ein Lebensweg. Deshalb bleiben Fragen zu Alltag, Familie, Unterkunft und Start im Team Teil unserer Arbeit.',
            },
          ],
        },
        {
          eyebrow: 'Warum das unser USP ist',
          title: 'Internationale Gewinnung gelingt nicht durch Reichweite allein.',
          text:
            'Der deutsche Arbeitsmarkt öffnet Wege für internationale Fachkräfte, gleichzeitig bleiben Anerkennung, Sprache und Integration anspruchsvoll. Viva Porta verbindet diese formalen Schritte mit persönlicher Begleitung und kultureller Übersetzung. Dadurch entsteht ein Prozess, der Bewerber:innen und Arbeitgeber früh auf dieselbe Realität vorbereitet.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--verified-user]',
              title: 'Verantwortungsvoll',
              text: 'Wir orientieren uns an fairen Grundsätzen internationaler Vermittlung: transparente Informationen, realistische Erwartungen und Respekt vor Herkunfts- und Zielland.',
            },
            {
              iconClass: 'icon-[material-symbols--medical-services]',
              title: 'Berufsnah',
              text: 'Gerade in regulierten Gesundheitsberufen zählt, dass Qualifikation, Sprache und Anerkennung früh zusammengedacht werden.',
            },
            {
              iconClass: 'icon-[material-symbols--groups]',
              title: 'Langfristig',
              text: 'Ein unterschriebener Vertrag ist nicht das Ziel allein. Entscheidend ist, ob Menschen im Team ankommen und Arbeitgeber verlässlich planen können.',
            },
          ],
        },
        {
          eyebrow: 'Gemeinsam stark',
          title: 'Mit Globus schaffen wir neue Einstiegspunkte für internationale Talente.',
          text:
            'Die Partnerschaft mit Globus zeigt, wie internationale Gewinnung konkret im Alltag funktionieren kann: Viva Porta begleitet Menschen strukturiert auf dem Weg nach Deutschland, während Globus als starker Arbeitgeber Perspektiven in service- und teamnahen Bereichen eröffnet. Gemeinsam entsteht ein verlässlicher Rahmen, in dem Vorbereitung, Ankommen und langfristige Zusammenarbeit zusammengedacht werden.',
          image: globusGemeinsam,
          imageAlt: 'Viva Porta und Globus gemeinsam bei der Partnerschaft',
        },
      ],
    },
    team: {
      key: 'team',
      eyebrow: 'Deine Ansprechpartner',
      title: 'Ein Team, das dich persönlich durch den Prozess begleitet.',
      intro:
        'Du brauchst Menschen, die erreichbar sind, deine Fragen ernst nehmen und den Weg nach Deutschland kennen. Unser Team unterstützt dich zweisprachig, strukturiert und mit Blick auf deinen langfristigen Erfolg.',
      cards: [],
      sections: [
        {
          eyebrow: 'Unser Hintergrund',
          title: 'Warum Viva Porta anders begleitet als reine Vermittlungsplattformen.',
          titleHighlight: 'Viva Porta',
          text:
            'Der Weg nach Deutschland ist für internationale Fachkräfte selten nur eine Bewerbung. Er besteht aus Anerkennung, Sprache, Behörden, Arbeitgebergesprächen, Familie, Alltag und vielen Entscheidungen, die sich in der Türkei anders anfühlen als in Deutschland. Genau hier liegt unser Unterschied: Wir kennen beide Perspektiven und machen aus einem komplexen Prozess eine verständliche Begleitung. Unser Team erklärt nicht nur, was als Nächstes passiert, sondern auch warum es wichtig ist, welche Erwartungen realistisch sind und wo früh Klarheit geschaffen werden muss.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--public]',
              title: 'Zwei Länder verstehen',
              text: 'Wir verbinden türkische Lebensrealität mit deutschen Anforderungen. Dadurch können wir Fragen, Sorgen und Missverständnisse früher einordnen als Anbieter, die nur den formalen Bewerbungsweg sehen.',
            },
            {
              iconClass: 'icon-[material-symbols--workspace-premium]',
              title: 'Anerkennung ernst nehmen',
              text: 'Gerade in regulierten Berufen entscheidet die Anerkennung darüber, wie schnell und passend ein beruflicher Start gelingt. Deshalb denken wir Qualifikation, Dokumente, Sprache und Arbeitgeberkontakt gemeinsam.',
            },
            {
              iconClass: 'icon-[material-symbols--psychology-alt]',
              title: 'Realistische Erwartungen',
              text: 'Wir versprechen keine Abkürzungen. Stattdessen sprechen wir offen über Dauer, Aufwand, Deutschlernen, Kostenfragen und die ersten Monate in Deutschland.',
            },
            {
              iconClass: 'icon-[material-symbols--support-agent]',
              title: 'Begleitung nach der Zusage',
              text: 'Unser USP endet nicht beim Vertrag. Ankunft, erste Behördengänge, Teamstart und Alltag bleiben Teil der Betreuung, weil genau dort Sicherheit und Vertrauen entstehen.',
            },
          ],
        },
      ],
      team: [...baseContent.team.members],
    },
    kontakt: {
      key: 'kontakt',
      eyebrow: 'Direkter Kontakt',
      title: 'Fragen? Sprich mit uns.',
      intro:
        'Erzähl uns dein Ziel. Wir klären die nächsten Schritte.',
      cards: [
        {
          iconClass: 'icon-[material-symbols--mail]',
          title: 'E-Mail',
          text: 'info@vivaporta.org',
        },
        {
          iconClass: 'icon-[material-symbols--call]',
          title: 'Telefon',
          text: '+49 176 42887719',
        },
        {
          iconClass: 'icon-[material-symbols--location-on]',
          title: 'Anschrift',
          text: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        },
      ],
      contact: {
        eyebrow: 'Persönliche Beratung',
        title: 'Kontaktieren Sie uns',
        email: 'info@vivaporta.org',
        phone: '+49 176 42887719',
        address: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        text: 'Erzähl uns kurz von deiner Ausbildung, deinem aktuellen Deutschstand und deinem Ziel. Wir melden uns mit einer ehrlichen Einschätzung und den nächsten Schritten für Bewerbung, Sprache, Anerkennung und Einreise.',
        mapEmbedUrl:
          'https://www.openstreetmap.org/export/embed.html?bbox=7.299%2C51.545%2C7.326%2C51.563&layer=mapnik&marker=51.554%2C7.312',
      },
    },
    impressum: {
      key: 'impressum',
      eyebrow: 'Impressum',
      title: 'Angaben gemäß gesetzlichen Informationspflichten.',
      intro:
        'Hier findest du die Anbieterkennzeichnung und die rechtlichen Pflichtinformationen der Viva Porta GmbH.',
      cards: [
        {
          iconClass: 'icon-[material-symbols--person]',
          title: 'Webseitenbetreiber',
          text: 'Enes Vural',
        },
        {
          iconClass: 'icon-[material-symbols--corporate-fare]',
          title: 'Unternehmen',
          text: 'Gesellschaft mit beschränkter Haftung (GmbH). Verantwortlicher: Enes Vural.',
        },
        {
          iconClass: 'icon-[material-symbols--location-on]',
          title: 'Anschrift',
          text: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        },
        {
          iconClass: 'icon-[material-symbols--alternate-email]',
          title: 'Kontakt',
          text: 'E-Mail: info@vivaporta.org. Telefon: +49 176 42887719.',
        },
        {
          iconClass: 'icon-[material-symbols--fact-check]',
          title: 'Unternehmensregistrierung',
          text: 'HRB 37337, Handelsregister B des Amtsgerichts Dortmund. Umsatzsteuer-Identifikationsnummer: DE455499883.',
        },
        {
          iconClass: 'icon-[material-symbols--gavel]',
          title: 'Online-Streitbeilegung',
          text: 'Die EU-Kommission stellt eine Plattform zur außergerichtlichen Online-Beilegung von Streitigkeiten bereit: https://ec.europa.eu/consumers/odr/. Hinweis gemäß § 36 Verbraucherstreitbeilegungsgesetz (VSBG).',
        },
      ],
    },
  };
  return pages;
};
